import useClientQueryString from "@/hooks/use-client-query-string";
import { useNavigate, useLocation } from "react-router-dom";
import { useRef, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import { trackLogout } from "@/lib/analytics/events";
import { LOGIN_TRACKED_SUB_KEY } from "@/features/authentication/constants";

type LoginParams = {
  postLoginRedirectionUrl?: string;
};

type UseLoginReturn = {
  onSuccessLogin: () => void;
  isSigninLoading: boolean;
  signIn: (params?: LoginParams) => void;
  signOut: () => void;
};

const useLogin = (): UseLoginReturn => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  // In MF mode there is no /:lng segment in the URL — read from i18n which
  // MFRootLayout keeps in sync with the host's active language.
  const lng = i18n.language || pathname.split("/")[1] || "ar";
  const postLoginRedirectionUrlRef = useRef<string>("");
  const { signoutRedirect, isLoading, activeNavigator } = useAuth();
  const clientQs = useClientQueryString();
  postLoginRedirectionUrlRef.current =
    clientQs.getByKey("postLoginRedirectionUrl") ?? "";

  const onSuccessLogin = useCallback(() => {
    if (activeNavigator === "signinSilent") return;

    const fromRef = postLoginRedirectionUrlRef.current || null;
    const fromUrl =
      new URLSearchParams(window.location.search).get(
        "postLoginRedirectionUrl",
      ) ?? `/${lng}`;
    const redirectUrl = fromRef ?? fromUrl;

    // In MF mode the BrowserRouter basename already includes /<lng>/travel,
    // so navigating to the full absolute URL would double the prefix.
    // Use navigate() for paths that fall inside the MFE's basename; fall back
    // to window.location.replace for absolute URLs from the host.
    const isStandalone = import.meta.env.VITE_STANDALONE !== "false";
    if (!isStandalone) {
      // Strip the basename (/ar/travel or /en/travel) so react-router
      // receives a path relative to its own root.
      const base = `/${lng}/travel`;
      const relativePath = redirectUrl.startsWith(base)
        ? redirectUrl.slice(base.length) || "/"
        : "/";
      navigate(relativePath, { replace: true });
    } else {
      window.location.replace(redirectUrl);
    }
  }, [activeNavigator, lng, navigate]);

  const signIn = useCallback(
    (params?: LoginParams) => {
      const { postLoginRedirectionUrl } = params ?? {};
      postLoginRedirectionUrlRef.current = postLoginRedirectionUrl ?? "";

      const hostAuth = (
        window as Window & { __HOST_AUTH__?: { signIn: () => void } }
      ).__HOST_AUTH__;
      if (hostAuth) {
        hostAuth.signIn();
        return;
      }

      if (!pathname.includes("/internal-login")) {
        const isStandalone = import.meta.env.VITE_STANDALONE !== "false";
        const loginPath = isStandalone
          ? `/${lng}/internal-login`
          : `/internal-login`;
        navigate(
          `${loginPath}?${new URLSearchParams({
            postLoginRedirectionUrl: postLoginRedirectionUrl ?? "",
          }).toString()}`,
        );
      }
    },
    [lng, navigate, pathname],
  );

  const signOut = useCallback(() => {
    if (isLoading) return;
    trackLogout();
    // Clear the GTM login dedupe marker so the next sign-in (same or different
    // user) fires a fresh `login` event. See PostLoginTracker.
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(LOGIN_TRACKED_SUB_KEY);
    }

    const hostAuth = (
      window as Window & { __HOST_AUTH__?: { signOut: () => void } }
    ).__HOST_AUTH__;
    if (hostAuth) {
      hostAuth.signOut();
      return;
    }

    signoutRedirect();
  }, [isLoading, signoutRedirect]);

  return {
    onSuccessLogin,
    isSigninLoading: isLoading || pathname.includes("/internal-login"),
    signIn,
    signOut,
  };
};

export default useLogin;
